import { Add, Delete, Edit } from '@mui/icons-material';
import { Button, Checkbox, FormControlLabel, Grid, useMediaQuery, Box } from '@mui/material';
import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import React, { FC, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router';
import { Button as SMButton, Loader, Table, Pagination } from '../../../../../components';
import { useSnackbar } from 'notistack';
import { UserContext } from '../../../../../context';
import { getJobTitles, deleteJobTitle } from '../../../../../fetch';
import { formatMoney, isAllowedToBeEditedOrDeleted } from '../../../../../helpers';
import { IUserJobTitle } from '../../../../../models';
import { ManagePartsModal } from '../../../../production/manage-roles/MangePartsModal';
import { MobileManageJobTitles } from './MobileManageJobTitles';

interface IManageJobTitles {}

export const ManageJobTitles: FC<IManageJobTitles> = () => {
  const { canEdit, isGlobalAdmin } = useContext(UserContext);
  const { enqueueSnackbar } = useSnackbar();
  const [jobTitles, setJobTitles] = useState<IUserJobTitle[]>([]);
  const [isLoadingJobTitles, setLoadingJobTitles] = useState<boolean>(false);
  const [selectedJobTitles, setSelectedJobTitles] = useState<IUserJobTitle[]>([]);
  const [managePartsModalIsOpen, setManagePartsModalIsOpen] = useState<boolean>(false);
  const [isDeleting, setDeleting] = useState<boolean>(false);
  const [page, setPage] = useState<number>(0);
  const [recordCount, setRecordCount] = useState<number>(0);
  const [perPage, setRowsPerPage] = useState<number>(10);
  const [sortDirection, setSortDirection] = useState<{
    DisplayName?: 'Asc' | 'Desc';
    IsPlaceOrderRole?: 'Asc' | 'Desc';
    MaterialSpendLimit?: 'Asc' | 'Desc';
    ChangeOrderSpendLimit?: 'Asc' | 'Desc';
  }>({
    DisplayName: 'Asc'
  });
  const [selectedSort, setSelectedSort] = useState<string>('DisplayName');

  const classes = useStyles();
  const isSmMobile = useMediaQuery('(max-width: 600px)');
  const history = useHistory();

  const handleClickColumn = useCallback(
    (column: string) => {
      setSelectedSort(column);
      setSortDirection({
        ...sortDirection,
        [column]: sortDirection[column] === 'Asc' ? 'Desc' : 'Asc'
      });
    },
    [sortDirection]
  );

  const fetchJobTitles = useCallback(async () => {
    try {
      setLoadingJobTitles(true);
      const res = await getJobTitles({
        page: page + 1,
        perPage: perPage,
        sortBy: selectedSort,
        sortDirection: sortDirection[selectedSort] as string
      });
      setJobTitles(res.records);
      setRecordCount(res.totalRecordCount || 0);
    } catch (error) {
      enqueueSnackbar('Error loading job titles. Please refresh and try again.', {
        variant: 'error'
      });
    } finally {
      setLoadingJobTitles(false);
    }
  }, [page, perPage, selectedSort, sortDirection, enqueueSnackbar]);

  useEffect(() => {
    fetchJobTitles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, perPage, selectedSort, sortDirection]);

  const handleDelete = useCallback(
    async (id: number) => {
      const result = window.confirm('Are you sure you want to delete this job title?');
      if (result) {
        setDeleting(true);
        try {
          await deleteJobTitle(id);
          enqueueSnackbar('Job title deleted successfully!', { variant: 'success' });
          fetchJobTitles();
        } catch (error) {
          const msg = error?.response?.data?.Detail || 'Unable to delete job title.';
          enqueueSnackbar(msg, { variant: 'error' });
        } finally {
          setDeleting(false);
        }
      }
    },
    [enqueueSnackbar, fetchJobTitles]
  );

  const columns = useMemo(() => {
    return [
      {
        id: 'checkbox',
        accessor: '',
        Header: '',
        sort: false,
        overrideWidth: 100,
        Cell: ({ cell }) => {
          const jobTitle = cell.row.original;
          const ids = selectedJobTitles.map(jt => jt.id);
          const isSelected = ids.includes(jobTitle.id);
          const showCheckbox =
            canEdit &&
            jobTitle.placeOrderRoleId !== null &&
            isAllowedToBeEditedOrDeleted(jobTitle.displayName);
          return (
            showCheckbox && (
              <FormControlLabel
                classes={{
                  label: classes.tableCheckbox
                }}
                color="primary"
                control={
                  <Checkbox
                    checked={isSelected}
                    id="job-titles-checkbox"
                    onChange={() => {
                      isSelected
                        ? setSelectedJobTitles(
                            selectedJobTitles.filter(
                              selectedJobTitle => selectedJobTitle.id !== jobTitle.id
                            )
                          )
                        : setSelectedJobTitles([...selectedJobTitles, jobTitle]);
                    }}
                  />
                }
                label=""
              />
            )
          );
        }
      },
      {
        Header: 'Job Title',
        accessor: 'displayName',
        isServerSorted: selectedSort === 'DisplayName',
        isServerSortedDesc: sortDirection.DisplayName === 'Desc',
        handleClickColumn: () => handleClickColumn('DisplayName')
      },
      {
        Header: 'Place Order Role',
        accessor: 'isPlaceOrderRole',
        isServerSorted: selectedSort === 'IsPlaceOrderRole',
        isServerSortedDesc: sortDirection.IsPlaceOrderRole === 'Desc',
        handleClickColumn: () => handleClickColumn('IsPlaceOrderRole'),
        Cell: ({ cell }) => {
          return <span>{cell.row.original.isPlaceOrderRole ? 'Yes' : 'No'}</span>;
        }
      },
      {
        Header: 'Materials Spend Limit',
        accessor: 'materialSpendLimit',
        isServerSorted: selectedSort === 'MaterialSpendLimit',
        isServerSortedDesc: sortDirection.MaterialSpendLimit === 'Desc',
        handleClickColumn: () => handleClickColumn('MaterialSpendLimit'),
        Cell: ({ cell }) => {
          const limit = cell.row.original.materialSpendLimit;
          return <>{limit !== null ? formatMoney(limit, 0) : ''}</>;
        }
      },
      {
        Header: 'Change Order Spend Limit',
        accessor: 'changeOrderSpendLimit',
        isServerSorted: selectedSort === 'ChangeOrderSpendLimit',
        isServerSortedDesc: sortDirection.ChangeOrderSpendLimit === 'Desc',
        handleClickColumn: () => handleClickColumn('ChangeOrderSpendLimit'),
        Cell: ({ cell }) => {
          const limit = cell.row.original.changeOrderSpendLimit;
          return <>{limit !== null ? formatMoney(limit, 0) : ''}</>;
        }
      },
      {
        id: 'actions',
        Header: '',
        accessor: '',
        sort: false,
        overrideWidth: isGlobalAdmin ? 200 : 100,
        Cell: ({
          cell: {
            row: { original }
          }
        }: {
          cell: { row: { original: IUserJobTitle } };
        }) => {
          return (
            canEdit &&
            isAllowedToBeEditedOrDeleted(original.displayName) && (
              <div className={classes.actionButtons}>
                <Button
                  color="primary"
                  startIcon={<Edit />}
                  className={classes.editButton}
                  onClick={async () => {
                    history.push(
                      `/admin/user-tools/unified-user-management/manage-roles-and-titles/job-titles/${original.id}`
                    );
                  }}
                >
                  Edit
                </Button>

                {isGlobalAdmin && original.canDelete && (
                  <Button
                    startIcon={<Delete />}
                    className={classes.deleteButton}
                    onClick={() => {
                      handleDelete(original.id);
                    }}
                  >
                    Delete
                  </Button>
                )}
              </div>
            )
          );
        }
      }
    ];
  }, [
    selectedJobTitles,
    canEdit,
    isGlobalAdmin,
    classes,
    history,
    handleDelete,
    selectedSort,
    sortDirection,
    handleClickColumn
  ]);

  return (
    <Box>
      {isDeleting && (
        <Loader position="centered" type="fullscreen">
          Deleting...
        </Loader>
      )}
      <Grid
        container
        alignItems="center"
        justifyContent={isSmMobile ? 'center' : 'space-between'}
        className={classes.addRoleWrapper}
      >
        {canEdit && (
          <SMButton
            id="edit-job-titles"
            startIcon={<Edit />}
            disabled={selectedJobTitles.length <= 0}
            onClick={() => {
              setManagePartsModalIsOpen(true);
            }}
            color="primary"
            variant="contained"
            className={classes.editRoleButton}
          >
            Edit
          </SMButton>
        )}
        {isGlobalAdmin && (
          <SMButton
            id="create-job-title"
            startIcon={<Add />}
            onClick={() =>
              history.push(
                '/admin/user-tools/unified-user-management/manage-roles-and-titles/job-titles/add-title'
              )
            }
            color="primary"
            variant="contained"
            className={classes.addRoleButton}
          >
            Add Job Title
          </SMButton>
        )}
      </Grid>
      <Table
        columns={columns}
        data={jobTitles}
        isLoading={isLoadingJobTitles}
        hidePagination
        ResponsiveComponent={isSmMobile ? MobileManageJobTitles : undefined}
        mobileProps={{
          handleDelete: isGlobalAdmin ? jobTitle => handleDelete(jobTitle) : null,
          handleEdit: canEdit
            ? (val: IUserJobTitle) =>
                history.push(
                  `/admin/user-tools/unified-user-management/manage-roles-and-titles/job-titles/${val.id}`
                )
            : null,
          canEdit
        }}
      />
      {!isLoadingJobTitles && (
        <Pagination
          page={page}
          count={recordCount}
          rowsPerPage={perPage}
          setPage={setPage}
          setRowsPerPage={setRowsPerPage}
        />
      )}
      <ManagePartsModal
        open={managePartsModalIsOpen}
        onClose={() => setManagePartsModalIsOpen(false)}
        roles={selectedJobTitles}
      />
    </Box>
  );
};

const useStyles = makeStyles((theme: Theme) => ({
  addRoleWrapper: {
    marginBottom: theme.spacing(1)
  },
  addRoleButton: {
    marginTop: theme.spacing(1),
    width: '100%',
    [theme.breakpoints.up('sm')]: {
      width: 'auto'
    }
  },
  editRoleButton: {
    alignSelf: 'flex-start',
    marginTop: theme.spacing(1),
    width: '100%',
    [theme.breakpoints.up('sm')]: {
      width: 'auto'
    }
  },
  actionButtons: {
    display: 'flex'
  },
  editButton: {},
  deleteButton: {
    color: theme.palette.error.main
  },
  tableCheckbox: {
    marginRight: 0
  },
  masterRow: {
    background: theme.palette.grey[200]
  }
}));
